import { useCreateOrganizationEquipmentSettingMutation } from '@/services/equipmentSettings';
import { isErrorWithData } from '@/services/helpers';
import { Box, Button, FormikTextInput, Modal, toast } from '@palmetto/palmetto-components';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { SyntheticEvent, useCallback } from 'react';
import * as yup from 'yup';

import { OrganizationSafeHarborPricingSettingsV2 } from 'types';

const validationSchema = yup.object({
  sku: yup.string().required('SKU is required'),
  equipmentPrice: yup
    .number()
    .required('Equipment Price is required')
    .min(0, 'Equipment Price must be a positive number'),
  shippingPrice: yup
    .number()
    .required('Shipping Price is required')
    .min(0, 'Shipping Price must be a positive number'),
});

interface CreateEquipmentSettingModalProps {
  isOpen: boolean;
  onClose: (event?: SyntheticEvent) => void;
  alias: string;
  programType: string;
}

interface FormValues {
  sku: string;
  equipmentPrice: number;
  shippingPrice: number;
}

const initialValues: FormValues = {
  sku: '',
  equipmentPrice: 0,
  shippingPrice: 0,
};

export const CreateEquipmentSettingModal = ({
  isOpen,
  onClose,
  alias,
  programType,
}: CreateEquipmentSettingModalProps) => {
  const [createEquipmentSetting] = useCreateOrganizationEquipmentSettingMutation();

  const onSubmit = useCallback(
    async (values: FormValues, { setSubmitting, resetForm }: FormikHelpers<FormValues>) => {
      try {
        const submitData: Omit<OrganizationSafeHarborPricingSettingsV2, 'status'> = {
          sku: values.sku,
          equipmentPrice: values.equipmentPrice,
          shippingPrice: values.shippingPrice,
        };
        await createEquipmentSetting({
          alias,
          programType,
          data: submitData,
        }).unwrap();
        toast.success('Equipment setting created successfully');
        resetForm();
        onClose();
      } catch (e) {
        if (isErrorWithData(e)) {
          const errorMessage = e.data.message;
          toast.error(`Error creating equipment setting: ${errorMessage}`);
        } else {
          console.error(e);
          toast.error('Error creating equipment setting');
        }
      } finally {
        setSubmitting(false);
      }
    },
    [alias, programType, createEquipmentSetting, onClose],
  );

  return (
    <Modal ariaLabelledBy="createEquipmentSetting" isOpen={isOpen} onDismiss={onClose} maxWidth="500px">
      <Modal.Header id="createEquipmentSetting" title="Create Equipment Setting" onDismiss={onClose} />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize
        validateOnBlur
      >
        {({ resetForm, isSubmitting }) => {
          return (
            <Form>
              <Modal.Body background="secondary">
                <Box direction="column" childGap="lg">
                  <Field component={FormikTextInput} label="SKU" name="sku" id="sku" isRequired autoComplete="off" />
                  <Field
                    component={FormikTextInput}
                    type="number"
                    prefix="$"
                    label="Equipment Price"
                    name="equipmentPrice"
                    id="equipmentPrice"
                    isRequired
                    autoComplete="off"
                  />
                  <Field
                    component={FormikTextInput}
                    type="number"
                    prefix="$"
                    label="Shipping Price"
                    name="shippingPrice"
                    id="shippingPrice"
                    isRequired
                    autoComplete="off"
                  />
                </Box>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  as="button"
                  onClick={() => {
                    resetForm();
                    onClose();
                  }}
                  variant="secondary"
                  tone="neutral"
                  size="md"
                  isLoading={isSubmitting}
                >
                  Cancel
                </Button>
                <Button variant="primary" size="md" type="submit" isLoading={isSubmitting}>
                  Create Setting
                </Button>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};
